/**
 * VAT Checkout analytics — SW-8538
 *
 * Thin wrappers around `sendAppEvent` that mirror the existing
 * "Subscribe Modal …" event-naming convention so the new VAT events
 * land in the same Mixpanel funnel.
 *
 * Events (see plan §9):
 *   - Subscribe Modal VAT Step Viewed
 *   - Subscribe Modal VAT Classification Submitted
 *   - Subscribe Modal VAT Checkout Error
 *   - Subscribe Modal VAT Step Skipped
 */
import { sendAppEvent } from "../../Utils";

/**
 * Fired when Step 2 (VAT Information) mounts.
 *
 * @param {object} props
 * @param {string | null} props.country
 * @param {"business" | "consumer"} props.classification
 * @param {string} props.source
 * @param {string | undefined} props.plan_id
 * @param {string | undefined} props.plan_interval
 */
export function sendVatStepViewed({ country, classification, source, plan_id, plan_interval }) {
  sendAppEvent("Subscribe Modal VAT Step Viewed", {
    country: country || null,
    classification,
    source,
    plan_id,
    plan_interval,
  });
}

/**
 * Fired when the user clicks "Continue to payment" on Step 2.
 *
 * `effective_outcome` is the resolved tax outcome from the Option B
 * rule in the flowchart:
 *   reverse_charge iff (hasVatId || (classification === "business" && businessAffirmation))
 *
 * @param {object} props
 * @param {string | null} props.country
 * @param {"business" | "consumer"} props.classification
 * @param {string} props.source
 * @param {boolean} props.has_vat_id
 * @param {boolean} props.affirmed_business
 * @param {"reverse_charge" | "vat_applied"} props.effective_outcome
 */
export function sendVatClassificationSubmitted({
  country,
  classification,
  source,
  has_vat_id,
  affirmed_business,
  effective_outcome,
}) {
  sendAppEvent("Subscribe Modal VAT Classification Submitted", {
    country: country || null,
    classification,
    source,
    has_vat_id,
    affirmed_business,
    effective_outcome,
  });
}

/**
 * Fired on any VAT checkout failure mode.
 *
 * @param {object} props
 * @param {string | null} props.country
 * @param {"business" | "consumer" | null} props.classification
 * @param {"geolocation" | "create_session" | "tax_id_rejected" | "missing_subscription"} props.error_type
 * @param {string | null | undefined} props.error_message
 */
export function sendVatCheckoutError({ country, classification, error_type, error_message }) {
  sendAppEvent("Subscribe Modal VAT Checkout Error", {
    country: country || null,
    classification: classification || null,
    error_type,
    error_message: error_message || null,
  });
}

/**
 * Fired when an EU user is NOT routed to Step 2 (non-EU, gate off,
 * geolocation unavailable, etc).
 *
 * @param {object} props
 * @param {"non_eu" | "geolocation_unavailable" | "gate_disabled"} props.reason
 */
export function sendVatStepSkipped({ reason }) {
  sendAppEvent("Subscribe Modal VAT Step Skipped", { reason });
}
